import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSystemStatusContext } from '../contexts/SystemStatusContext';
import type { MaintenanceState } from '../types/system';
import '../styles/system-status.css';

const MAINT_DISMISS_KEY = 'wt_maintenance_dismissed';

function formatEndsAt(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function maintenanceFingerprint(maintenance: MaintenanceState): string {
  return `${maintenance.enabled}:${maintenance.message}:${maintenance.endsAt ?? ''}`;
}

function isMaintenanceDismissed(maintenance: MaintenanceState): boolean {
  try {
    return sessionStorage.getItem(MAINT_DISMISS_KEY) === maintenanceFingerprint(maintenance);
  } catch {
    return false;
  }
}

function dismissMaintenance(maintenance: MaintenanceState): void {
  try {
    sessionStorage.setItem(MAINT_DISMISS_KEY, maintenanceFingerprint(maintenance));
  } catch {
    // ignore
  }
}

export default function SystemStatusBanner() {
  const { status, updateAvailable, reloadForUpdate, acknowledgeUpdate } = useSystemStatusContext();
  const [maintenanceDismissed, setMaintenanceDismissed] = useState(false);

  const maintenance = status?.maintenance;
  const maintenanceKey = maintenance ? maintenanceFingerprint(maintenance) : '';

  useEffect(() => {
    if (!maintenance?.enabled) {
      setMaintenanceDismissed(false);
      return;
    }
    setMaintenanceDismissed(isMaintenanceDismissed(maintenance));
  }, [maintenanceKey, maintenance?.enabled]);

  const showPreprod = status?.appEnv === 'preprod';
  const showMaintenance = Boolean(maintenance?.enabled) && !maintenanceDismissed;
  const endsAtLabel = maintenance ? formatEndsAt(maintenance.endsAt) : null;

  const hasOverlay = Boolean(
    status && (updateAvailable || showPreprod || showMaintenance),
  );

  const handleDismissMaintenance = useCallback(() => {
    if (!maintenance) return;
    dismissMaintenance(maintenance);
    setMaintenanceDismissed(true);
  }, [maintenance]);

  if (!status || !hasOverlay) return null;

  return createPortal(
    <div className="system-status-overlay-stack" role="status" aria-live="polite">
      {updateAvailable && (
        <div className="system-status-toast system-status-toast--update" role="alert">
          <div className="system-status-toast-body">
            <strong>Nouvelle version disponible</strong>
            <span>Build {status.buildId} — rechargez pour profiter des dernières corrections.</span>
          </div>
          <div className="system-status-toast-actions">
            <button
              type="button"
              className="system-status-btn system-status-btn--primary"
              onClick={() => reloadForUpdate(status.buildId)}
            >
              Recharger
            </button>
            <button
              type="button"
              className="system-status-btn"
              onClick={() => acknowledgeUpdate(status.buildId)}
            >
              Plus tard
            </button>
          </div>
        </div>
      )}

      {showPreprod && (
        <div className="system-status-toast system-status-toast--preprod">
          Environnement <strong>preprod</strong> — données de test, version {status.buildId}
        </div>
      )}

      {showMaintenance && maintenance && (
        <div className="system-status-toast system-status-toast--maintenance" role="alert">
          <div className="system-status-toast-body">
            <strong>Maintenance</strong>
            <span>{maintenance.message}</span>
            {endsAtLabel && (
              <span className="system-status-ends-at">Reprise prévue vers {endsAtLabel}</span>
            )}
          </div>
          <div className="system-status-toast-actions">
            <button
              type="button"
              className="system-status-btn"
              onClick={handleDismissMaintenance}
            >
              Masquer
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
